/**
 * CONTRACT.md §2.8 — forms, over react-hook-form.
 *
 * `ARCHITECTURE.md` §Frontend: "react-hook-form with the zod schema from
 * `packages/shared` as resolver; the same schema the API validates with. Errors
 * from the API's `errors` field map back onto fields." So every control here
 * takes a `control` and a `name` and reads its own state through
 * `useController` — explicitly, as an argument, rather than through a context a
 * page cannot see. `useZodForm(schema)` is the one-line wrapper that pairs the
 * resolver with the schema.
 *
 * ## What each control renders
 *
 * A control renders its whole `Field`: the `<label>`, the input, and either its
 * hint or its error. **The error is a `Hint` with `role="alert"`** and the input
 * points at it with `aria-describedby` — CONTRACT.md has no error class, and it
 * does not need one: §2.8's `.hint` is the muted line under a field, and what
 * makes this one an error is that it is announced.
 *
 * `Switch` and `Drop` are the two that are not native controls, and both say why
 * at their definition.
 */

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { useId, useLayoutEffect, useRef, type ReactNode } from 'react'
import {
  useController,
  useForm,
  type Control,
  type FieldPath,
  type FieldValues,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form'
import type { z } from 'zod'

import { Button, ButtonRow } from './Button.js'
import { cx, type Common } from './common.js'
import { continueList, toggleList, type ListKind, type TextEdit } from './listText.js'

/* --------------------------------------------------------------- the form */

/**
 * `useForm` with a zod resolver, and nothing else.
 *
 * The schema is the one `packages/shared/src/schemas/` publishes and the API
 * validates with, so a field the API would refuse is refused here first and in
 * the same words. `standardSchemaResolver` rather than `zodResolver` because
 * zod 4 implements Standard Schema and that is the resolver that speaks it —
 * one adapter for the one thing the schema promises.
 *
 * The form holds the schema's **input** shape, which is what a half-filled form
 * is: `z.input` and `z.output` differ the moment a field has a `.default()`, and
 * the value a control is bound to is the one before the default is applied.
 */
export function useZodForm<Input extends FieldValues, Output>(
  schema: z.ZodType<Output, Input>,
  options: Omit<UseFormProps<Input, unknown, Output>, 'resolver'> = {},
): UseFormReturn<Input, unknown, Output> {
  return useForm<Input, unknown, Output>({
    ...options,
    resolver: standardSchemaResolver(schema),
  })
}

/* ------------------------------------------------------- the layout pieces */

export type FieldProps = Common & { readonly children: ReactNode }

/** The `--sp-3` block a label, a control and its hint live in. */
export function Field({ children, ...common }: FieldProps) {
  return (
    <div className="field" {...common}>
      {children}
    </div>
  )
}

export type LabelProps = Common & {
  readonly htmlFor: string
  readonly children: ReactNode
}

/** Plain sans, muted (§3: "not mono now"). Styled by `.field > label`. */
export function Label({ htmlFor, children, ...common }: LabelProps) {
  return (
    <label htmlFor={htmlFor} {...common}>
      {children}
    </label>
  )
}

export type HintProps = Common & {
  /** Announces itself. This is what makes a hint an error. */
  readonly alert?: boolean | undefined
  readonly children: ReactNode
}

export function Hint({ alert, children, ...common }: HintProps) {
  return (
    <div className="hint" role={alert === true ? 'alert' : undefined} {...common}>
      {children}
    </div>
  )
}

/* -------------------------------------------------------- the wired controls */

type Wired<Values extends FieldValues> = {
  readonly control: Control<Values>
  readonly name: FieldPath<Values>
  readonly label: ReactNode
  /** The muted line under the control, shown when there is no error. */
  readonly hint?: ReactNode | undefined
}

/**
 * What a text control should show for whatever the form is holding.
 *
 * A number is a number: `defaultValues: { blocks: 7 }` with `type="number"` used
 * to render an empty box over a form state of 7, so the reader saw "empty",
 * typed, and overwrote a value they were never shown. Anything that is neither a
 * string nor a number — an object, a `File`, `undefined` — has no text form and
 * renders as empty, which is what an unset field looks like.
 */
function asText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

/**
 * The four lines every control repeats: its ids, its message, and the two ARIA
 * attributes that tie them together. Extracted because getting
 * `aria-describedby` wrong is invisible and getting it right is mechanical.
 */
function useFieldState<Values extends FieldValues>(
  control: Control<Values>,
  name: FieldPath<Values>,
  hint: ReactNode,
) {
  const { field, fieldState } = useController({ control, name })
  const id = useId()
  const messageId = `${id}-message`
  const error = fieldState.error?.message
  const invalid = error !== undefined

  return {
    field,
    id,
    invalid,
    describedBy: invalid || hint !== undefined ? messageId : undefined,
    message:
      invalid || hint !== undefined ? (
        <Hint alert={invalid} id={messageId}>
          {invalid ? error : hint}
        </Hint>
      ) : null,
  }
}

/** One box the browser says holds no value, and what to say under it. */
export type HalfTyped = { readonly name: string; readonly message: string }

/**
 * The boxes in a form that are **not empty and hold no value**, by field name.
 *
 * A `datetime-local` with one segment missing and a number box holding `1e` both
 * report their value as `''` — exactly what they report when somebody has emptied
 * them on purpose — and `validity.badInput` is the only thing that tells the two
 * apart. A form's state cannot: React hears of a box only when its value changes,
 * and the value does not change between "half typed" and "emptied". So this is read
 * off the elements, at the moment the form is submitted, by the form that draws
 * them.
 *
 * It matters wherever an empty box *means* something. `ActionForm` sends a box that
 * opened with a value and is blank now as that value being taken away, so a date
 * with its time backspaced out — "the 14th, no time" — was saved as **no date at
 * all**, and the page said so as if it had been asked to. The list's own date cell
 * has refused the same thing since it was written (`components/studio/CellEdit.tsx`
 * `refusedBy`); this is the generic form's half.
 *
 * The name is the control's `name` attribute, which every control here sets to its
 * react-hook-form path, so the answer goes straight into `setError`.
 */
export function halfTypedIn(form: HTMLFormElement): readonly HalfTyped[] {
  return Array.from(form.querySelectorAll('input'))
    .filter((input) => input.name !== '' && input.validity.badInput)
    .map((input) => ({ name: input.name, message: HALF_TYPED[input.type] ?? HALF_TYPED_FALLBACK }))
}

/** The `type` a form marks a half-typed box's error with, so it can tell its own apart. */
export const HALF_TYPED_ERROR = 'half-typed'

/**
 * The name of the box an event came from, **if that box is no longer half typed** —
 * or null: it still is, or the event came from something that is not a named box.
 *
 * The other half of `halfTypedIn`, for the same blind spot. A box told "finish it, or
 * empty the box" that is then emptied goes from `''` to `''`: no change event, so the
 * form never hears that the reader did what it asked and the refusal stays under an
 * empty box. A key going up and the cursor leaving are the two moments the box can
 * have stopped being bad input without its value changing.
 */
export function settledBox(target: EventTarget): string | null {
  if (!(target instanceof HTMLInputElement) || target.name === '') return null
  return target.validity.badInput ? null : target.name
}

/**
 * What a half-typed box is told, per kind — a whole sentence, because it replaces
 * the hint while it shows (CONTRACT.md §2.8 gives a field one muted line).
 */
const HALF_TYPED: Readonly<Record<string, string>> = {
  'datetime-local':
    'That is not a whole date and time yet, so nothing was saved. Fill in the part that is ' +
    'missing, or empty the box.',
  number: 'That is not a number, so nothing was saved. Type one, or empty the box.',
}

const HALF_TYPED_FALLBACK =
  'This box does not hold a whole value yet, so nothing was saved. Finish it, or empty the box.'

export type InputProps<Values extends FieldValues> = Wired<Values> & {
  /**
   * `datetime-local` is session 24's (R3-09): `due_at` was a free-text box over a
   * field whose schema is RFC 3339, so `next Friday` came back as the raw Zod
   * "Invalid ISO datetime" — a refusal about a wire format, at somebody answering the
   * question the label had asked. A control the platform already gets right cannot
   * produce a shape the API refuses.
   *
   * `password` is session 46's, and it is not about a password: it is a GitHub token
   * for a site hook, which is write-only (`packages/shared` `GithubTokenSchema`) — the
   * portal never shows it back, so the box that takes it does not show it either, to
   * the person behind the one typing it.
   */
  readonly type?:
    'text' | 'email' | 'tel' | 'url' | 'number' | 'datetime-local' | 'password' | undefined
  readonly placeholder?: string | undefined
  readonly autoComplete?: string | undefined
  readonly disabled?: boolean | undefined
  /** HTML `min` — used for `type="number"` when the schema publishes a floor. */
  readonly min?: number | undefined
  /** HTML `max` — used for `type="number"` when the schema publishes a ceiling. */
  readonly max?: number | undefined
}

export function Input<Values extends FieldValues>({
  control,
  name,
  label,
  hint,
  type,
  placeholder,
  autoComplete,
  disabled,
  min,
  max,
}: InputProps<Values>) {
  const { field, id, invalid, describedBy, message } = useFieldState(control, name, hint)
  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      <input
        className="input"
        id={id}
        name={field.name}
        ref={field.ref}
        type={type ?? 'text'}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled ?? field.disabled}
        min={min}
        max={max}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        value={asText(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
      {message}
    </Field>
  )
}

export type SelectOption = { readonly value: string; readonly label: string }

/**
 * A named run of options — one `<optgroup>`.
 *
 * Session 24 (R2-08): the personal-timezone control is 419 options, and a flat list
 * of 419 opens on `Africa/Abidjan` with nothing to navigate by. Grouping is what the
 * platform already has for that, and it is what makes a native `<select>`'s own
 * type-ahead legible — the list is ten chunks the reader recognises rather than one
 * alphabet. No new primitive: `mockups/assets/CONTRACT.md` §5 refused a filter bar
 * and `Overlay.tsx`'s `SelectMenu` is a studio control, so an `<optgroup>` inside the
 * `<select>` this form field already is remains the smallest true answer.
 */
export type SelectGroup = { readonly label: string; readonly options: readonly SelectOption[] }

/** Either — a `Select` takes a mixed list, so a lone option may precede the groups. */
export type SelectChoice = SelectOption | SelectGroup

function isSelectGroup(choice: SelectChoice): choice is SelectGroup {
  return 'options' in choice
}

/** Every option a list offers, groups flattened. What "is this value one of ours" reads. */
function optionsOf(choices: readonly SelectChoice[]): readonly SelectOption[] {
  return choices.flatMap((choice) => (isSelectGroup(choice) ? choice.options : [choice]))
}

export type SelectProps<Values extends FieldValues> = Wired<Values> & {
  readonly options: readonly SelectChoice[]
  /** What the empty state says, when the form is holding a value no option has. */
  readonly placeholder?: string | undefined
  readonly disabled?: boolean | undefined
}

/**
 * A native `<select>`. `Overlay.tsx`'s `SelectMenu` is the Radix one, for a
 * studio control that needs a searchable or a decorated list; a form field that
 * is a list of words is better as the thing the platform already gets right on
 * a phone.
 *
 * **The placeholder is not decoration.** A `<select>` whose `value` matches no
 * option shows its *first* option, so a field with no default rendered "Social
 * content" over a form state of `undefined` — the reader saw a filled field, the
 * schema refused it on submit, and leaving the visible choice alone fired no
 * change event to fix it. A disabled placeholder is what an unset select looks
 * like, and it cannot be chosen back.
 */
export function Select<Values extends FieldValues>({
  control,
  name,
  label,
  hint,
  options,
  placeholder,
  disabled,
}: SelectProps<Values>) {
  const { field, id, invalid, describedBy, message } = useFieldState(control, name, hint)
  const value = asText(field.value)
  const chosen = optionsOf(options).some((option) => option.value === value)

  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      <select
        className="select"
        id={id}
        name={field.name}
        ref={field.ref}
        disabled={disabled ?? field.disabled}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        value={chosen ? value : ''}
        onChange={field.onChange}
        onBlur={field.onBlur}
      >
        {chosen ? null : (
          <option value="" disabled>
            {placeholder ?? 'Choose one'}
          </option>
        )}
        {options.map((choice) =>
          isSelectGroup(choice) ? (
            <optgroup key={`group:${choice.label}`} label={choice.label}>
              {choice.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ),
        )}
      </select>
      {message}
    </Field>
  )
}

export type TextareaProps<Values extends FieldValues> = Wired<Values> & {
  readonly placeholder?: string | undefined
  readonly disabled?: boolean | undefined
  /**
   * Lists, written in the text: a *Bulleted list* and a *Numbered list* button over
   * the box, and Enter on a list line starts the next item (`listText.ts`). The value
   * stays plain text — a list is the lines a person would have typed — which is why
   * this is an option on the plain box rather than the `Editor`, whose document is
   * JSON and would need a reader everywhere the text is printed.
   */
  readonly lists?: boolean | undefined
  /**
   * Enter sends the form and Shift+Enter starts a new line — a chat's box, for a reply in a
   * comment thread (session 55), where a comment is a few lines and sending is the usual next
   * thing. Never with `lists`, whose Enter is the next item.
   */
  readonly submitOnEnter?: boolean | undefined
  /** Focused when it appears — a box opened by the gesture that asked for it. */
  readonly autoFocus?: boolean | undefined
}

/** The words on the two list buttons. */
export const LIST_BUTTON_LABEL: Readonly<Record<ListKind, string>> = {
  bullet: 'Bulleted list',
  number: 'Numbered list',
}

const LIST_KINDS: readonly ListKind[] = ['bullet', 'number']

export function Textarea<Values extends FieldValues>({
  control,
  name,
  label,
  hint,
  placeholder,
  disabled,
  lists,
  submitOnEnter,
  autoFocus,
}: TextareaProps<Values>) {
  const { field, id, invalid, describedBy, message } = useFieldState(control, name, hint)
  const box = useRef<HTMLTextAreaElement | null>(null)

  /*
   * Where the selection goes after an edit this box made itself. The value is the
   * form's, so the new text arrives on the next render — and a selection set before
   * then is set on the old text and thrown away when React writes the new one.
   */
  const selection = useRef<{ readonly start: number; readonly end: number } | null>(null)
  useLayoutEffect(() => {
    const at = selection.current
    const element = box.current
    if (at === null || element === null) return
    selection.current = null
    element.focus()
    element.setSelectionRange(at.start, at.end)
  })

  const off = disabled ?? field.disabled
  const text = asText(field.value)

  function apply(edit: TextEdit): void {
    // The same text is no change, so no render would come to place the selection —
    // and one left waiting would jump the caret on the next key somebody pressed.
    if (edit.text === text) {
      box.current?.focus()
      box.current?.setSelectionRange(edit.start, edit.end)
      return
    }
    selection.current = { start: edit.start, end: edit.end }
    field.onChange(edit.text)
  }

  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      {lists === true ? (
        <ButtonRow>
          {LIST_KINDS.map((kind) => (
            <Button
              key={kind}
              size="sm"
              variant="ghost"
              disabled={off}
              aria-controls={id}
              onClick={() => {
                const element = box.current
                const start = element?.selectionStart ?? text.length
                const end = element?.selectionEnd ?? text.length
                apply(toggleList(text, start, end, kind))
              }}
            >
              {LIST_BUTTON_LABEL[kind]}
            </Button>
          ))}
        </ButtonRow>
      ) : null}
      <textarea
        className="textarea"
        id={id}
        name={field.name}
        ref={(element) => {
          box.current = element
          field.ref(element)
        }}
        placeholder={placeholder}
        disabled={off}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        value={text}
        onChange={field.onChange}
        onBlur={field.onBlur}
        autoFocus={autoFocus}
        onKeyDown={
          lists === true || submitOnEnter === true
            ? (event) => {
                // An IME composing a character owns its own Enter.
                if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
                if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return
                const target = event.currentTarget
                if (lists !== true) {
                  event.preventDefault()
                  target.form?.requestSubmit()
                  return
                }
                const edit = continueList(target.value, target.selectionStart, target.selectionEnd)
                if (edit === null) return
                event.preventDefault()
                apply(edit)
              }
            : undefined
        }
      />
      {message}
    </Field>
  )
}

export type CheckProps<Values extends FieldValues> = Wired<Values> & {
  readonly disabled?: boolean | undefined
}

/**
 * A checkbox and its sentence, on one `.check` row.
 *
 * The row is a `<label>` and the message is its sibling, so the markup is the
 * contract's when there is nothing to say and grows a `.hint` when there is. It
 * has one because a required box — `z.literal(true)` on an approval — is a field
 * that can be the only thing stopping a submit, and a form that refuses in
 * silence is a form nobody can finish.
 */
export function Check<Values extends FieldValues>({
  control,
  name,
  label,
  hint,
  disabled,
}: CheckProps<Values>) {
  const { field, invalid, describedBy, message } = useFieldState(control, name, hint)
  return (
    <>
      <label className="check">
        <input
          type="checkbox"
          name={field.name}
          ref={field.ref}
          disabled={disabled ?? field.disabled}
          checked={field.value === true}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={field.onChange}
          onBlur={field.onBlur}
        />
        <span>{label}</span>
      </label>
      {message}
    </>
  )
}

export type RadioCheckProps<Values extends FieldValues> = Wired<Values> & {
  /** The value this row stands for. The group is every row sharing a `name`. */
  readonly value: string
  /**
   * Renders the group's error and its hint under this row. **Set it on the last
   * row of the group, and on no other.** A radio group is one field spread over
   * several rows, so every row sees the same `fieldState` — without this the
   * message would be printed once per choice, and without it anywhere a group
   * that fails validation says nothing at all.
   */
  readonly showError?: boolean | undefined
  readonly disabled?: boolean | undefined
}

/**
 * One choice of a radio group, on a `.check` row. There is no `.radio` class:
 * §2.8's `.check` is the row, and what is in it is the platform's control.
 */
export function RadioCheck<Values extends FieldValues>({
  control,
  name,
  label,
  hint,
  value,
  showError,
  disabled,
}: RadioCheckProps<Values>) {
  const { field, invalid, describedBy, message } = useFieldState(control, name, hint)
  return (
    <>
      <label className="check">
        <input
          type="radio"
          name={field.name}
          ref={field.ref}
          value={value}
          disabled={disabled ?? field.disabled}
          checked={field.value === value}
          aria-invalid={invalid}
          aria-describedby={showError === true ? describedBy : undefined}
          onChange={() => {
            field.onChange(value)
          }}
          onBlur={field.onBlur}
        />
        <span>{label}</span>
      </label>
      {showError === true ? message : null}
    </>
  )
}

export type SwitchProps<Values extends FieldValues> = Wired<Values> & {
  readonly disabled?: boolean | undefined
}

/**
 * The 32×18 toggle from `client/settings.html`.
 *
 * A `<button role="switch">` and not an `<input type="checkbox">`, because
 * `.switch` is drawn with `::after` — the knob — and a replaced element renders
 * no pseudo-elements at all. The label is a sibling `<span>` tied on with
 * `aria-labelledby` rather than a wrapping `<label>`, because a `<label>` does
 * not forward a click to a button.
 */
export function Switch<Values extends FieldValues>({
  control,
  name,
  label,
  hint,
  disabled,
}: SwitchProps<Values>) {
  const { field, invalid, describedBy, message } = useFieldState(control, name, hint)
  const id = useId()
  const on = field.value === true

  return (
    <>
      <div className="check">
        <button
          className={cx('switch', on && 'is-on')}
          type="button"
          role="switch"
          aria-checked={on}
          aria-labelledby={id}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          disabled={disabled ?? field.disabled}
          onClick={() => {
            field.onChange(!on)
          }}
          onBlur={field.onBlur}
          ref={field.ref}
        />
        <span id={id}>{label}</span>
      </div>
      {message}
    </>
  )
}

export type DropProps = Common & {
  readonly label: ReactNode
  /** The `.xs` line inside the box — "Briefs, footage, references — up to 2 GB". */
  readonly hint?: ReactNode | undefined
  readonly accept?: string | undefined
  readonly multiple?: boolean | undefined
  readonly onFiles: (files: readonly File[]) => void
}

/**
 * The dashed drop box. Not wired to react-hook-form: an upload is a request of
 * its own (`files.register`, session 16), not a field on a form body.
 *
 * The mockup's `<span class="linkish">browse</span>` is a `<button>` here. Drag
 * and drop is an enhancement no keyboard has, so the affordance that opens the
 * file picker has to be focusable — and the `<input type="file">` behind it is
 * hidden, because styling one to look like this box is what `.drop` exists to
 * avoid.
 */
export function Drop({ label, hint, accept, multiple, onFiles, ...common }: DropProps) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <div
      className="drop"
      onDragOver={(event) => {
        event.preventDefault()
      }}
      onDrop={(event) => {
        event.preventDefault()
        onFiles([...event.dataTransfer.files])
      }}
      {...common}
    >
      <span>
        {label}{' '}
        <button
          className="linkish"
          type="button"
          onClick={() => {
            input.current?.click()
          }}
        >
          browse
        </button>
      </span>
      {hint === undefined ? null : (
        <>
          <br />
          <span className="xs">{hint}</span>
        </>
      )}
      <input
        type="file"
        hidden
        ref={input}
        accept={accept}
        multiple={multiple}
        onChange={(event) => {
          onFiles([...(event.target.files ?? [])])
          /*
           * Cleared, or the same file cannot be chosen twice: the input still
           * holds the last selection, so picking it again is not a change and
           * fires no event. Session 16's flow — pick, remove from the pending
           * list, pick the same file again — is exactly that sequence.
           */
          event.target.value = ''
        }}
      />
    </div>
  )
}
